import React, { useCallback, useState } from 'react';
import ReactMultiSelect, { components } from 'react-select';
import ReactCreatableMultiSelect from 'react-select/creatable';
import classnames from 'classnames';
import isNil from 'lodash/isNil';
import type {
    ActionMeta,
    ControlProps,
    MultiValueGenericProps,
    MultiValueRemoveProps,
    OnChangeValue,
} from 'react-select';

import '../Select_.scss';
import './MultiSelect_.scss';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { NotValidFieldText } from '@components/NotValidFieldText';
import { FieldTitle } from '@components/FieldTitle';
import { ScrollBar } from '@components/ScrollBar';
import { makeCustomOption } from '@utils/makeCustomOption';

import { DropdownIndicator, noOptionsMessage, OptionType } from '../Select';

import { Icon, IconType } from '@components/Icon';
import { translate } from '@chaika/i18n/helper';
import { MenuList } from '@components/Select/MultiSelect/components/MenuList';
import { normalizeYoFilter } from '@utils/normalizeYoString';
import { LeftTitle } from '@components/LeftTitle';
import { useMultiSelect } from './hooks/useMultiSelect';

// https://github.com/JedWatson/react-select#props
export interface MultiSelectBaseProps {
    betterFiltering?: boolean;
    dataTest?: string;
    options: OptionType[];
    name?: string;
    title?: string;
    value?: OptionType[];
    placeholder?: string;
    forcePlaceholder?: boolean;
    disabled?: boolean;
    isValid?: boolean;
    clearAfterSelect?: boolean;
    notValidText?: string;
    isSearchable?: boolean;
    isCreatable?: boolean;
    leftTitle?: boolean;
    minSelected?: number;
    modifiers?: MultiSelectModifiers[];
    menuPortalTarget?: HTMLElement | null;
    onMenuOpen?(): void;
}

export interface MultiSelectProps extends MultiSelectBaseProps {
    selectedValues?: string[] | number[];
    customValues?: string[];
    onChange?(selectedItem: Nullable<OptionType[]>, name?: string): void;
    onCreateOption?(value: OptionType[], name?: string): void;
}

export enum MultiSelectModifiers {
    // @ts-ignore
    InheritWidth = 'component_multi_select__inheritWidth',
    // @ts-ignore
    RedesignedLabel = 'component_multi_select__redesignedLabel',
}

/* eslint-disable */
export function MultiSelect({
    dataTest,
    leftTitle,
    customValues,
    modifiers = [],
    options,
    title,
    value,
    selectedValues,
    disabled,
    placeholder,
    forcePlaceholder,
    isSearchable = true,
    isValid,
    notValidText,
    isCreatable,
    name,
    onChange: onChangeProp,
    onCreateOption: onCreateOptionProp,
    onMenuOpen,
    minSelected = 0,
    clearAfterSelect,
    menuPortalTarget,
}: MultiSelectProps) {
    const {
        isOnFocus,
        value: resolvedValue,
        placeholder: resolvedPlaceholder,
        handleFocus: onFocus,
        handleBlur: onBlur,
        onChange,
        onCreateOption,
    } = useMultiSelect({
        options,
        value,
        selectedValues,
        customValues,
        placeholder,
        title,
        forcePlaceholder,
        name,
        onChange: onChangeProp,
        onCreateOption: onCreateOptionProp,
    });

    const withValidStatus = !isNil(isValid);
    const [inputValue, setInputValue] = useState('');

    const isAtMinSelected = resolvedValue.length <= minSelected;
    const LockedMultiValueRemove = isAtMinSelected ? NoOp : MultiValueRemove;

    const handleCreateOption = useCallback(
        (label: string) => {
            setInputValue('');
            onCreateOption([makeCustomOption(label)], name);
        },
        [name, onCreateOption],
    );

    const handleChange = useCallback(
        (selectedItem: Nullable<OptionType[]>, name?: string) => {
            if (minSelected > 0 && (!selectedItem || selectedItem.length < minSelected)) return;
            onChange(selectedItem, name);
            if (clearAfterSelect) {
                setInputValue('');
            }
        },
        [onChange, clearAfterSelect, minSelected],
    );

    const handleSelectChange = useCallback(
        (selectedItem: OnChangeValue<OptionType, true>, _actionMeta: ActionMeta<OptionType>) =>
            handleChange(selectedItem.length ? [...selectedItem] : null, name),
        [handleChange, name],
    );

    const onInputChange = (value: string) => {
        setInputValue(value);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, inputValue?: string) => {
        if (event.key === 'Enter' && !!inputValue) {
            event.preventDefault();

            if (isCreatable) {
                const hasValue = !!customValues?.length && customValues.some(customValue => customValue === inputValue);

                if (!hasValue) {
                    const customOption = makeCustomOption(inputValue);
                    setInputValue('');
                    return onCreateOption([customOption], name);
                } else {
                    setInputValue('');
                }
            }
        }
    };

    const select = (
        <div className={classnames(['component_multi_select', ...modifiers])}>
            <div
                data-test={dataTest}
                data-testid={dataTest}
                className={classnames(['component_select__root', 'component_multi_select__root'])}
            >
                {title && <div className={classnames(['title'])}>{!leftTitle && <FieldTitle title={title} />}</div>}
                {isCreatable ? (
                    <ReactCreatableMultiSelect
                        isMulti
                        placeholder={resolvedPlaceholder ?? '-'}
                        inputValue={inputValue}
                        onInputChange={onInputChange}
                        options={options}
                        value={resolvedValue}
                        onChange={handleSelectChange}
                        isSearchable={isSearchable}
                        isClearable={false}
                        menuPlacement={'auto'}
                        menuPortalTarget={menuPortalTarget}
                        classNames={
                            menuPortalTarget
                                ? { menuPortal: () => 'component_select__root component_multi_select' }
                                : undefined
                        }
                        noOptionsMessage={noOptionsMessage}
                        isDisabled={disabled}
                        filterOption={normalizeYoFilter}
                        className={'component_select__root'}
                        classNamePrefix={'react-select'}
                        menuIsOpen={isOnFocus && !disabled}
                        components={{
                            Control,
                            DropdownIndicator,
                            MenuList,
                            MultiValueContainer,
                            MultiValueRemove: LockedMultiValueRemove,
                        }}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onKeyDown={(event: any) => handleKeyDown(event, event.target.value)}
                        onCreateOption={handleCreateOption}
                        isOptionDisabled={(option: any) => Boolean(resolvedValue.some(v => v.value === option.value))}
                        onMenuOpen={onMenuOpen}
                        formatCreateLabel={(inputValue: string) => `${translate('create')} «${inputValue}»`}
                    />
                ) : (
                    <ReactMultiSelect
                        isMulti
                        inputValue={inputValue}
                        placeholder={resolvedPlaceholder ?? '-'}
                        onInputChange={onInputChange}
                        options={options}
                        value={resolvedValue}
                        onChange={handleSelectChange}
                        isSearchable={isSearchable}
                        isClearable={false}
                        menuPlacement={'auto'}
                        menuPortalTarget={menuPortalTarget}
                        classNames={
                            menuPortalTarget
                                ? { menuPortal: () => 'component_select__root component_multi_select' }
                                : undefined
                        }
                        noOptionsMessage={noOptionsMessage}
                        isDisabled={disabled}
                        className={'component_select__root'}
                        classNamePrefix={'react-select'}
                        menuIsOpen={isOnFocus && !disabled}
                        components={{
                            Control,
                            DropdownIndicator,
                            MenuList,
                            MultiValueContainer,
                            MultiValueRemove: LockedMultiValueRemove,
                        }}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onMenuOpen={onMenuOpen}
                        filterOption={normalizeYoFilter}
                        onKeyDown={(event: any) => handleKeyDown(event, event.target.value)}
                    />
                )}
                {withValidStatus && !isValid && notValidText && <NotValidFieldText title={notValidText} />}
            </div>
        </div>
    );

    return leftTitle && title ? <LeftTitle title={title}>{select}</LeftTitle> : select;
}

export const MultiValueContainer = (props: MultiValueGenericProps<OptionType, true>) => {
    return (
        <div className={'component_multi_select__value'}>
            <components.MultiValueContainer {...props} />
        </div>
    );
};

export const Control = (props: ControlProps<OptionType, true>) => {
    return (
        <div className={'component_multi_select__control'}>
            <ScrollBar autoHeight>
                <components.Control {...props} />
            </ScrollBar>
        </div>
    );
};

const NoOp = () => null;

export const MultiValueRemove = (props: MultiValueRemoveProps<OptionType, true>) => {
    return (
        <components.MultiValueRemove {...props}>
            <div className={'component_multi_select__remover'}>
                <div className={'component_multi_select__icon_wrapper'}>
                    <Icon type={IconType.CROSS} />
                </div>
            </div>
        </components.MultiValueRemove>
    );
};
